import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Navbar from '../components/Navbar';
import { useBranding } from '../context/BrandingContext';
import { Plus, UserX, UserCheck, Save, RefreshCw } from 'lucide-react';
import CompanyAssignments from '../components/CompanyAssignments';
import './CompanyAdmin.css';

// The customer's own administration page. Everything here is bounded by their
// company on the server, so there is nothing on screen that could name another.
// Nothing about the device platform appears anywhere - as far as this page is
// concerned, machines simply belong to the company.
const CompanyAdmin = () => {
  const { displayName } = useBranding();

  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [devices, setDevices] = useState([]);

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = async () => {
    try {
      const [c, u, d, m] = await Promise.all([
        api.get('/company'),
        api.get('/company/users'),
        api.get('/company/dashboards'),
        api.get('/company/devices')
      ]);
      setCompany(c.data);
      setUsers(u.data);
      setDashboards(d.data);
      setDevices(m.data);
    } catch (e) {
      setError('Could not load your company');
    }
  };

  useEffect(() => { load(); }, []);

  // Their own machine list, brought back in line with the device platform.
  // Safe for them to run: it reads only their own company's customer.
  const refreshMachines = async () => {
    setBusy('refresh'); setError(null); setMessage(null);
    try {
      const res = await api.post('/company/devices/refresh');
      const { total, added, removed } = res.data;
      setMessage(
        added || removed
          ? `${total} machine${total === 1 ? '' : 's'} (${added} added, ${removed} removed).`
          : `${total} machine${total === 1 ? '' : 's'}; nothing changed.`
      );
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not refresh the machines');
    } finally {
      setBusy(null);
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    setBusy('new'); setError(null); setMessage(null);
    try {
      await api.post('/company/users', form);
      setMessage(`${form.username} can now sign in.`);
      setForm({ username: '', email: '', password: '' });
      setAdding(false);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.error || 'Could not add that person');
    } finally {
      setBusy(null);
    }
  };

  const setActive = async (user, active) => {
    setBusy(user.id); setError(null); setMessage(null);
    try {
      if (active) {
        await api.put(`/company/users/${user.id}`, { is_active: true });
      } else {
        await api.delete(`/company/users/${user.id}`);
      }
      setMessage(`${user.username} ${active ? 'can sign in again' : 'can no longer sign in'}.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not change that account');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="company-admin-page">
      <Navbar />

      <div className="company-admin-content">
        <h1>{company ? company.name : displayName}</h1>

        <div className="company-summary">
          <div className="summary-tile">
            <span className="summary-value">{users.length}</span>
            <span className="summary-label">people</span>
          </div>
          <div className="summary-tile">
            <span className="summary-value">{devices.length}</span>
            <span className="summary-label">machine{devices.length === 1 ? '' : 's'}</span>
          </div>
          <div className="summary-tile">
            <span className="summary-value">{dashboards.length}</span>
            <span className="summary-label">dashboard{dashboards.length === 1 ? '' : 's'}</span>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {message && <div className="alert alert-success">{message}</div>}

        <div className="section-header">
          <h2>People</h2>
          <button className="btn btn-primary" onClick={() => setAdding((v) => !v)}>
            <Plus size={18} /> Add person
          </button>
        </div>

        {adding && (
          <form className="add-person" onSubmit={addUser}>
            <div className="form-group">
              <label htmlFor="new-username">Username</label>
              <input
                id="new-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-email">Email</label>
              <input
                id="new-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">Password</label>
              <input
                id="new-password"
                type="password"
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
              <span className="form-help">At least 8 characters.</span>
            </div>
            <div className="add-person-actions">
              <button className="btn btn-primary" type="submit" disabled={busy === 'new'}>
                <Save size={16} /> {busy === 'new' ? 'Adding...' : 'Add'}
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="table-scroll">
          <table className="people-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Can sign in</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.is_active ? '' : 'row-inactive'}>
                  <td>
                    {u.username}
                    {u.role !== 'user' && <span className="role-chip">administrator</span>}
                  </td>
                  <td>{u.email}</td>
                  <td>{u.is_active ? 'Yes' : 'No'}</td>
                  <td className="row-actions">
                    {u.role === 'user' && (
                      u.is_active ? (
                        <button
                          className="btn btn-danger btn-small"
                          disabled={busy === u.id}
                          onClick={() => setActive(u, false)}
                        >
                          <UserX size={15} /> Suspend
                        </button>
                      ) : (
                        <button
                          className="btn btn-secondary btn-small"
                          disabled={busy === u.id}
                          onClick={() => setActive(u, true)}
                        >
                          <UserCheck size={15} /> Restore
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={4} className="empty">Nobody here yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="section-header">
          <h2>Machines</h2>
          <button className="btn btn-secondary" disabled={busy === 'refresh'} onClick={refreshMachines}>
            <RefreshCw size={18} /> {busy === 'refresh' ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="table-scroll">
          <table className="people-table">
            <thead>
              <tr><th>Machine</th><th>Type</th><th>Last checked</th></tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.tb_device_id}>
                  <td>
                    {d.label || d.device_name || d.tb_device_id}
                    {d.label && <span className="machine-ref">{d.device_name}</span>}
                  </td>
                  <td>{d.profile || 'unknown'}</td>
                  <td>{d.synced_at ? new Date(d.synced_at).toLocaleString() : '-'}</td>
                </tr>
              ))}
              {devices.length === 0 && (
                <tr><td colSpan={3} className="empty">No machines assigned yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <CompanyAssignments users={users} dashboards={dashboards} onChanged={load} />
      </div>
    </div>
  );
};

export default CompanyAdmin;
